import React from 'react';
import { Target, Users, Zap, ArrowUpRight } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';

const services = [
  {
    number: '01',
    title: 'Influencer Marketing',
    description: 'Access 1,000+ creators across Finance, Tech, Lifestyle, and more. Hand-picked matches for every campaign.',
    icon: Target,
  },
  {
    number: '02',
    title: 'Talent Management',
    description: 'Exclusive representation for top creators. We handle deals, content strategy, and long-term growth.',
    icon: Users,
  },
  {
    number: '03',
    title: 'Campaign Strategy',
    description: 'End-to-end planning, briefs, content review, and reporting. Measurable results, every time.',
    icon: Zap,
  },
];

export const About = () => {
  const { settings } = useSettings();
  const s = settings || {};
  return (
    <section id="about" className="py-24 md:py-32 bg-gradient-to-b from-orange-100 via-pink-50 to-rose-100 relative overflow-hidden">
      {/* Decorative orbs */}
      <div className="absolute top-20 -left-32 w-96 h-96 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-60"></div>
      <div className="absolute bottom-20 -right-32 w-[500px] h-[500px] bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
      <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-rose-200 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #000 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      ></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Editorial header — asymmetric */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 mb-16 md:mb-20">
          <div className="lg:col-span-5">
            <p className="text-sm font-bold tracking-[0.2em] text-orange-600 uppercase mb-4">
              {s.about_eyebrow || 'What we do'}
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.05]">
              {s.about_headline_line1 || 'Built for brands.'}
              <br />
              <span className="italic font-serif text-orange-600">{s.about_headline_italic || 'Loved'}</span>{' '}
              {s.about_headline_line2 || 'by creators.'}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
              {s.about_description || "We're not another agency. We're the bridge between bold brands and authentic creators — delivering campaigns that resonate, convert, and build long-term equity."}
            </p>
          </div>
        </div>

        {/* Services — minimal cards with serial numbers */}
        <div className="grid md:grid-cols-3 gap-px bg-gray-200 border border-gray-200 rounded-2xl overflow-hidden">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                className="bg-white p-8 lg:p-10 hover:bg-orange-50/50 transition-colors duration-300 group relative"
                data-testid={`service-card-${index}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="text-xs font-bold text-gray-400 tracking-widest">
                    {service.number}
                  </span>
                  <div className="w-12 h-12 rounded-full bg-gray-100 group-hover:bg-orange-600 flex items-center justify-center transition-colors duration-300">
                    <Icon
                      className="text-gray-700 group-hover:text-white transition-colors duration-300"
                      size={20}
                    />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3 leading-tight">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
                <div className="mt-6 inline-flex items-center text-sm font-semibold text-orange-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn more
                  <ArrowUpRight size={16} className="ml-1" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
